import { defineStore } from "pinia";
import {
  AuthTokens,
  fetchAuthSession,
  getCurrentUser,
  signOut,
  updateUserAttributes,
} from "@aws-amplify/auth";
import { ability, abilityVersion } from "boot/ability";
import defineRules, { getSubject } from "src/services/AppAbility";

let restorePromise: Promise<void> | null = null;

interface State {
  tokens: AuthTokens | undefined;
  initialized: boolean;
  tokenLogin: boolean;
}

export type UserSession = {
  email?: string;
  firstname?: string;
  surname?: string;
  mainLocation?: string;
  route?: string;
  locations?: string[];
};

export const useSessionStore = defineStore("session", {
  state: (): State => {
    return {
      tokens: undefined,
      initialized: false,
      tokenLogin: false,
    };
  },
  getters: {
    hasSession: async () => {
      return (await fetchAuthSession()).credentials?.sessionToken != undefined;
    },
    isAuthorized: async (state) => {
      return state.tokens;
    },
    isTokenAuthorized: (state) => {
      return state.tokenLogin;
    },
    isInitialized: (state) => {
      return state.initialized;
    },
    getEmail: (state) => {
      return state.tokens?.idToken?.payload["email"];
    },
    getEmailVerified: (state) => {
      return state.tokens?.idToken?.payload["email_verified"];
    },
    getFirstName: (state) => {
      return state.tokens?.idToken?.payload["given_name"];
    },
    getLocations: (state) => {
      const locations =
        state.tokens?.idToken?.payload["custom:locations"]?.toString();
      if (!locations) {
        return [];
      } else {
        return locations.split(",");
      }
    },
    getMainLocation: (state) => {
      return state.tokens?.idToken?.payload["custom:mainLocation"];
    },
    getRoute: (state) => {
      return state.tokens?.idToken?.payload["custom:route"]?.toString();
    },
    getSurname: (state) => {
      return state.tokens?.idToken?.payload["family_name"];
    },
    getUsername: (state) => {
      return state.tokens?.idToken?.payload["cognito:username"];
    },
    getUserGroups: (state) => {
      return (state.tokens?.idToken?.payload["cognito:groups"] ??
        []) as string[];
    },
  },

  actions: {
    async initialize(token?: string) {
      if (!token) {
        this.tokenLogin = false;
        await this.refreshSession(true);
      } else {
        this.tokenLogin = true;
        ability.update(defineRules(["Dashboard"]));
        abilityVersion.value++;
      }
      this.initialized = true;
    },

    async logout() {
      // Drop the memoized restore so the router guard re-checks the session
      // instead of trusting the pre-logout result.
      restorePromise = null;
      try {
        await signOut();
      } finally {
        this.tokens = undefined;
        this.initialized = false;
        this.redirectLogin();
      }
    },

    restore(): Promise<void> {
      if (restorePromise) return restorePromise;
      restorePromise = (async () => {
        try {
          await getCurrentUser();
          await this.refreshSession();
        } catch {
          // not signed in — clear any stale tokens so guards see it too
          this.tokens = undefined;
        }
      })();
      return restorePromise;
    },

    async refreshSession(forge: boolean = false) {
      const session = await fetchAuthSession({
        forceRefresh: forge,
      });
      if (!session.tokens) {
        this.redirectLogin();
      }

      this.tokens = session.tokens;
      const groups = (session.tokens?.idToken?.payload["cognito:groups"] ??
        []) as string[];
      ability.update(defineRules(groups));
      abilityVersion.value++;
    },

    redirectLogin() {
      this.router.push({ name: getSubject("Login") });
    },

    async updateUser(value: {
      firstname?: string;
      surname?: string;
      email?: string;
    }) {
      await updateUserAttributes({
        userAttributes: {
          ...(value.firstname ? { given_name: value.firstname } : {}),
          ...(value.surname ? { family_name: value.surname } : {}),
        },
      });
      await this.initialize();
    },
  },
});
